
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Smile, Upload, Check, XCircle, Camera, UserCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface SignupStepTwoProps {
  avatars: string[];
  selectedAvatar: string;
  setSelectedAvatar: (url: string) => void;
  avatarTab: "preset" | "custom";
  setAvatarTab: (tab: "preset" | "custom") => void;
  customAvatarPreview: string;
  clearCustomAvatar: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const SignupStepTwo = ({
  avatars,
  selectedAvatar,
  setSelectedAvatar,
  avatarTab,
  setAvatarTab,
  customAvatarPreview,
  clearCustomAvatar,
  fileInputRef,
  handleFileChange
}: SignupStepTwoProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground mb-4">
          {t('auth.avatarDescription')}
        </p>
        
        <div className="mx-auto w-14 h-14 rounded-full overflow-hidden border border-gray-300 bg-muted flex items-center justify-center">
          {avatarTab === "preset" && selectedAvatar ? (
            <div className="w-full h-full flex items-center justify-center">
              <img src={selectedAvatar} alt="Selected avatar" className="w-10 h-10 object-cover" />
            </div>
          ) : avatarTab === "custom" && customAvatarPreview ? (
            <div className="w-full h-full flex items-center justify-center">
              <img src={customAvatarPreview} alt="Custom avatar" className="w-full h-full object-cover" />
            </div>
          ) : (
            <Smile className="w-6 h-6 text-primary" />
          )}
        </div>
      </div>

      <Tabs value={avatarTab} onValueChange={value => setAvatarTab(value as "preset" | "custom")} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="preset">
            <Smile className="mr-2 h-4 w-4" />
            {t('auth.presetAvatars')}
          </TabsTrigger>
          <TabsTrigger value="custom">
            <Upload className="mr-2 h-4 w-4" />
            {t('auth.uploadPhoto')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="preset" className="mt-4">
          <ScrollArea className="h-48 rounded-md border">
            <div className="grid grid-cols-4 gap-4 p-4">
              {avatars.map(avatar => (
                <button 
                  key={avatar} 
                  type="button" 
                  onClick={() => setSelectedAvatar(avatar)} 
                  className={cn(
                    "relative w-14 h-14 rounded-full overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all border border-gray-300", 
                    selectedAvatar === avatar && "ring-2 ring-primary"
                  )}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <img src={avatar} alt={t('profile.avatarOption')} className="w-10 h-10 object-cover" />
                  </div>
                  {selectedAvatar === avatar && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="custom" className="mt-4">
          <div className="flex flex-col items-center space-y-4">
            {customAvatarPreview ? (
              <div className="relative w-32 h-32">
                <Avatar className="w-32 h-32 border-2 border-primary">
                  <AvatarImage src={customAvatarPreview} className="object-cover" />
                  <AvatarFallback>
                    <UserCircle2 className="w-12 h-12 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                
                <Button 
                  type="button" 
                  variant="destructive" 
                  size="icon" 
                  className="absolute -top-2 -right-2 h-8 w-8 rounded-full" 
                  onClick={clearCustomAvatar}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="w-32 h-32 rounded-full border-2 border-dashed border-primary/40 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors" 
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="h-10 w-10 text-primary/60 mb-2" />
                <span className="text-xs text-center text-muted-foreground px-2">
                  {t('profile.clickToUpload')}
                </span>
              </div>
            )}
            
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
            
            <Button 
              type="button" 
              variant="outline" 
              className="w-full" 
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              {customAvatarPreview ? t('profile.changePhoto') : t('profile.uploadPhoto')}
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              {t('profile.supportedFormats')}<br />
              {t('profile.maximumSize')}
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
